import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { activeProfileRoles, isGuardOnlyProfile } from '../lib/guardProfile';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { playNotificationSound, primeNotificationSound } from '../lib/notificationSound';
import InteractionSafety from './InteractionSafety';
import {
  LayoutDashboard,
  Building2,
  Users,
  ClipboardCheck,
  Clock,
  AlertTriangle,
  FileText,
  FolderOpen,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronDown,
  ChevronRight,
  Shield,
  Receipt,
  Landmark,
  Home,
  BarChart3,
  ListChecks,
  Calendar,
  Handshake,
  FileBarChart,
  BriefcaseBusiness,
  LifeBuoy,
  Wrench,
  Package,
  QrCode,
  Sparkles,
  ShieldCheck,
  Search,
  Plus,
  Link2,
  GraduationCap,
  MessageCircle,
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import GlobalSupportCoach from './GlobalSupportCoach';
import WebPushPrompt from './WebPushPrompt';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';

// Mapeo de rutas a módulos
const MODULO_MAP = {
  '/direccion': null,
  '/configuracion': null,
  '/asistencia': 'asistencia',
  '/calendario-operativo': 'calendario_operativo',
  '/checklists': 'checklists',
  '/incidencias': 'incidencias',
  '/documentos': 'documentos',
  '/alertas': 'alertas',
  '/condo-ar': 'financiero',
  '/administracion': null,
  '/proveedores': 'proveedores',
  '/cotizaciones': 'cotizaciones',
  '/soporte': 'soporte_ar',
  '/mantenimientos': 'mantenimientos',
  '/areas-comunes': 'areas_comunes',
  '/gestion-corporativa': 'gestion_corporativa',
  '/inventario': 'inventario',
  '/gestion-residentes': null,
  '/administracion/residentes': null,
  '/atencion-vecinal': 'atencion_vecinal',
  '/access-control': 'access_control',
  '/ares-seguridad': 'access_control',
  '/sanciones': 'sanciones',
  '/gestiones-consejo': null,
  '/transparencia-financiera': null,
};

const navigation = [
  { name: 'Inicio', href: '/dashboard', icon: LayoutDashboard, roles: [], modulo: null },
  { name: 'Consejo', href: '/consejo', icon: ShieldCheck, roles: ['consejo'], modulo: null },
  { name: 'Conoce AR', href: '/conoce-ar/consejo', icon: GraduationCap, roles: ['consejo'], modulo: null },
  { name: 'Control administrativo', href: '/control-integral', icon: ShieldCheck, roles: ['direccion'], modulo: null },
  { name: 'Continuidad operativa', href: '/continuidad', icon: Link2, roles: ['direccion', 'supervisor', 'administrador'], modulo: null },
  { name: 'Centro de Dirección', href: '/direccion', icon: Sparkles, roles: ['direccion'], modulo: null },
  { name: 'Configuración', href: '/configuracion', icon: Settings, roles: ['direccion'], modulo: null },
  { name: 'Calendario Operativo', href: '/calendario-operativo', icon: Calendar, roles: [], modulo: 'calendario_operativo' },
  { name: 'Gestión operativa AR/Ares', href: '/gestion-corporativa', icon: Building2, roles: ['direccion'], modulo: 'gestion_corporativa' },
  { name: 'Usuarios', href: '/usuarios', icon: Users, roles: ['supervisor'], modulo: null },
  { name: 'Unidades y residentes', href: '/administracion/unidades', icon: Home, roles: ['direccion', 'supervisor', 'administrador'], modulo: null },
  { name: 'Gestiones del Consejo', href: '/gestiones-consejo', icon: MessageCircle, roles: ['direccion', 'supervisor', 'administrador'], modulo: null },
  { name: 'Caja Chica', href: '/caja-chica', icon: Receipt, roles: ['direccion', 'supervisor', 'administrador', 'consejo'], modulo: 'caja_chica' },
  { name: 'Mis Finanzas', href: '/condo-ar/finanzas', icon: Landmark, roles: ['direccion', 'supervisor', 'administrador', 'consejo'], modulo: 'financiero' },
  { name: 'Proveedores', href: '/proveedores', icon: BriefcaseBusiness, roles: ['direccion', 'supervisor', 'administrador', 'consejo'], modulo: 'proveedores' },
  { name: 'Cotizaciones', href: '/cotizaciones', icon: FileBarChart, roles: ['direccion', 'supervisor', 'administrador', 'consejo'], modulo: 'cotizaciones' },
  { name: 'Mantenimientos', href: '/mantenimientos', icon: Wrench, roles: ['direccion', 'supervisor', 'administrador', 'consejo'], modulo: 'mantenimientos' },
  { name: 'Áreas Comunes', href: '/areas-comunes', icon: Home, roles: ['direccion', 'supervisor', 'administrador', 'consejo'], modulo: 'areas_comunes' },
  { name: 'Inventario', href: '/inventario', icon: Package, roles: ['direccion', 'supervisor', 'administrador', 'consejo'], modulo: 'inventario' },
  { name: 'Atención Vecinal', href: '/atencion-vecinal', icon: QrCode, roles: ['direccion', 'supervisor', 'administrador'], modulo: 'atencion_vecinal' },
  { name: 'Ares Seguridad', href: '/ares-seguridad', icon: Shield, roles: ['direccion', 'supervisor', 'administrador', 'guardia'], modulo: 'access_control' },
  { name: 'Sanciones', href: '/sanciones', icon: AlertTriangle, roles: ['direccion', 'supervisor', 'administrador', 'consejo'], modulo: 'sanciones' },
  { name: 'Asistencia', href: '/asistencia', icon: Clock, roles: [], modulo: 'asistencia' },
  // Checklists ahora es submenú colapsable
  { name: 'Incidencias', href: '/incidencias', icon: AlertTriangle, roles: [], modulo: 'incidencias' },
  { name: 'Biblioteca y comunicados', href: '/documentos', icon: FolderOpen, roles: [], modulo: 'documentos' },
  { name: 'Notificaciones', href: '/alertas', icon: Bell, roles: [], modulo: 'alertas' },
  { name: 'Soporte AR', href: '/soporte', icon: LifeBuoy, roles: ['direccion', 'supervisor', 'administrador'], modulo: 'soporte_ar' },
  { name: 'Academia Grupo AR', href: '/academia', icon: GraduationCap, roles: ['direccion', 'supervisor', 'administrador'], modulo: null },
];

const checklistsNav = [
  { name: 'Ejecuciones', href: '/checklists', icon: ClipboardCheck, roles: ['direccion', 'supervisor', 'administrador', 'guardia', 'jardineria', 'mantenimiento'] },
  { name: 'Plantillas', href: '/checklists/plantillas', icon: ListChecks, roles: ['direccion', 'supervisor', 'administrador'] },
];

// NEW: Navigation for Juntas/Minutas module
const juntasNav = [
  { name: 'Minutas', href: '/minutas', icon: FileText, roles: [] },
  { name: 'Dashboard de Acuerdos', href: '/acuerdos', icon: Handshake, roles: [] },
];

// NEW: Navigation for Reportes module
const reportesNav = [
  { name: 'Reportes', href: '/reportes', icon: FileBarChart, roles: [] },
  { name: 'Transparencia financiera', href: '/reportes/transparencia', icon: ShieldCheck, roles: ['direccion', 'supervisor', 'administrador'] },
  { name: 'KPIs Operativos', href: '/reportes/operacion-kpis', icon: BarChart3, roles: ['direccion'] },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ATTENTION_ROUTE_RULES = [
  { route: '/asistencia', patterns: ['asistencia'] },
  { route: '/incidencias', patterns: ['incidencia'] },
  { route: '/checklists', patterns: ['checklist'] },
  { route: '/documentos', patterns: ['documento', 'comunicado', 'poliza', 'contrato'] },
  { route: '/mantenimientos', patterns: ['mantenimiento'] },
  { route: '/areas-comunes', patterns: ['area_comun', 'reservacion', 'reserva'] },
  { route: '/inventario', patterns: ['inventario', 'stock', 'caducidad'] },
  { route: '/atencion-vecinal', patterns: ['vecinal', 'reporte_vecinal', 'solicitud'] },
  { route: '/ares-seguridad', patterns: ['acceso', 'visita', 'paquete', 'rondin', 'guardia', 'ares', 'seguridad'] },
  { route: '/sanciones', patterns: ['sancion', 'apercibimiento'] },
  { route: '/proveedores', patterns: ['proveedor'] },
  { route: '/cotizaciones', patterns: ['cotizacion'] },
  { route: '/caja-chica', patterns: ['caja_chica', 'comprobante'] },
  { route: '/minutas', patterns: ['minuta', 'junta'] },
  { route: '/acuerdos', patterns: ['acuerdo'] },
  { route: '/condo-ar/finanzas?seccion=cobranza', patterns: ['cargo', 'cuota', 'cartera', 'cobranza'] },
  { route: '/condo-ar/finanzas?seccion=ingresos', patterns: ['ingreso', 'pago_no_identificado', 'pago_sin_identificar'] },
  { route: '/condo-ar/finanzas?seccion=egresos', patterns: ['egreso', 'cuenta_por_pagar', 'aprobacion_egreso'] },
  { route: '/condo-ar/finanzas?seccion=banco', patterns: ['banco', 'movimiento_bancario', 'estado_bancario', 'conciliacion'] },
  { route: '/reportes', patterns: ['cierre_financiero', 'diferencia_bancaria'] },
  { route: '/condo-ar/finanzas?seccion=configuracion', patterns: ['configuracion_financiera', 'vigencia', 'cuenta_bancaria'] },
  { route: '/reportes', patterns: ['reporte'] },
];

const attentionRouteFor = (alerta) => {
  if (String(alerta?.entidad_tipo || '').toLowerCase() === 'guard_finding' && alerta?.entidad_id) {
    const params = new URLSearchParams({ novedad: alerta.entidad_id });
    if (alerta?.condominio_id) params.set('condominio', alerta.condominio_id);
    return `/ares-seguridad/reportes?${params.toString()}`;
  }
  if (String(alerta?.entidad_tipo || '').toLowerCase() === 'direction_instruction') {
    return '/dashboard#comunicacion-direccion';
  }
  if (String(alerta?.tipo || '').startsWith('asistencia_recordatorio_')) {
    const tipo = String(alerta.tipo).endsWith('_salida') ? 'salida' : 'entrada';
    const params = new URLSearchParams({ tipo, via: 'notificacion' });
    if (alerta?.condominio_id) params.set('condominio', alerta.condominio_id);
    return `/asistencia/registrar?${params.toString()}`;
  }
  const signature = [alerta?.tipo, alerta?.entidad_tipo, alerta?.titulo]
    .filter(Boolean).join(' ').toLocaleLowerCase('es-MX').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return ATTENTION_ROUTE_RULES.find((rule) => rule.patterns.some((pattern) => signature.includes(pattern)))?.route || '/alertas';
};

const isOperationallyActionableAlert = (alerta) => {
  const type = String(alerta?.tipo || '').toLowerCase();
  // Estos registros informan actividad o evaluaciones disponibles, pero no
  // representan un movimiento pendiente dentro del módulo señalado.
  if (type.startsWith('inactividad_') || type === 'evaluacion_operativa') return false;
  return Boolean(alerta?.entidad_id || alerta?.accion_url || alerta?.action_url);
};

const EXPERIENCE_ROUTES = [
  { match: '/checklists/ejecucion', id: 'checklist-ejecucion', eyebrow: 'OPERACIÓN EN CAMPO', title: 'Ejecución de checklist', description: 'Captura avances y evidencias con una secuencia clara, táctil y enfocada en completar el trabajo.', icon: ClipboardCheck },
  { match: '/checklists/plantillas', id: 'checklist-plantillas', eyebrow: 'ESTANDAR OPERATIVO', title: 'Plantillas de checklist', description: 'Disena procesos reutilizables con instrucciones legibles, responsables claros y menor carga de captura.', icon: ListChecks },
  { match: '/checklists', id: 'checklists', eyebrow: 'CONTROL OPERATIVO', title: 'Ejecuciones de checklist', description: 'Prioriza lo pendiente, identifica excepciones y completa cada recorrido desde computadora o celular.', icon: ClipboardCheck },
  { match: '/calendario-operativo', id: 'calendario', title: 'Calendario', description: 'Actividades, reuniones y vencimientos.', icon: Calendar },
  { match: '/juntas/calendario-actividades', id: 'calendario', title: 'Calendario', description: 'Actividades, reuniones y vencimientos.', icon: Calendar },
  { match: '/incidencias', id: 'incidencias', eyebrow: 'SEGUIMIENTO PUNTUAL', title: 'Incidencias', description: 'Distingue urgencias, responsables, plazos y evidencias para intervenir antes de que un caso se estanque.', icon: AlertTriangle },
  { match: '/gestion-residentes', id: 'gestion-residentes', eyebrow: 'RELACION CON RESIDENTES', title: 'Gestión de residentes', description: 'Administra unidades, personas, portal, permisos y canales desde una sola fuente por condominio.', icon: Users },
  { match: '/atencion-vecinal', id: 'atencion-vecinal', title: 'Atención vecinal', description: 'Reportes y solicitudes de residentes.', icon: QrCode },
  { match: '/asistencia', id: 'asistencia', title: 'Asistencia', description: 'Entradas, salidas y faltas del personal.', icon: Clock },
  { match: '/documentos', id: 'documentos', eyebrow: 'COMUNICACIÓN INSTITUCIONAL', title: 'Biblioteca y comunicados', description: 'Consulta formatos, organiza contenido institucional y crea comunicados profesionales con apoyo de IA.', icon: FolderOpen },
  { match: '/sanciones', id: 'sanciones', eyebrow: 'TRAZABILIDAD NORMATIVA', title: 'Sanciones y apercibimientos', description: 'Prepara expedientes sustentados, revisables y faciles de seguir sin perder el control humano.', icon: Shield },
  { match: '/proveedores', id: 'proveedores', eyebrow: 'RED DE SERVICIO', title: 'Proveedores', description: 'Evalua cobertura, cumplimiento y documentacion con una ficha ejecutiva consistente y accionable.', icon: BriefcaseBusiness },
  { match: '/mantenimientos', id: 'mantenimientos', eyebrow: 'CONTINUIDAD OPERATIVA', title: 'Mantenimiento', description: 'Ordena necesidades, trabajos, evidencias y cierres con prioridades visibles para todo el equipo.', icon: Wrench },
  { match: '/cotizaciones', id: 'cotizaciones', eyebrow: 'DECISION COMPARABLE', title: 'Cotizaciones', description: 'Compara propuestas sobre una misma base y presenta al Consejo una recomendacion clara y justificada.', icon: FileBarChart },
  { match: '/inventario', id: 'inventario', eyebrow: 'CONTROL PATRIMONIAL', title: 'Inventario', description: 'Localiza existencias, movimientos y alertas con una experiencia visual enfocada en exactitud operativa.', icon: Package },
  { match: '/minutas', id: 'minutas', eyebrow: 'JUNTAS Y SEGUIMIENTO', title: 'Minutas', description: 'Registra la reunion en tiempo real y transforma conversaciones en acuerdos claros con menos captura.', icon: FileText },
  { match: '/acuerdos', id: 'acuerdos', eyebrow: 'PANORAMA EJECUTIVO', title: 'Dashboard de acuerdos', description: 'Identifica cumplimiento, responsables, categorias y riesgos para dirigir el seguimiento con evidencia.', icon: Handshake },
];
const CORE_EXPERIENCE = { id: 'core' };

const OPERATION_SEARCH_SHORTCUTS = [
  { label: 'Pagos pendientes de validar', href: '/condo-ar/finanzas?seccion=ingresos', terms: ['pago', 'pagos', 'ingreso', 'comprobante', 'validar'], icon: Receipt },
  { label: 'Vehículos, residentes y unidades', href: '/administracion/unidades', terms: ['casa', 'unidad', 'residente', 'telefono', 'vehiculo', 'placa', 'tag', 'mascota', 'personal'], icon: Home },
  { label: 'Reportes e incidencias abiertas', href: '/incidencias', terms: ['reporte', 'incidencia', 'queja', 'falla', 'urgente'], icon: AlertTriangle },
  { label: 'Reservaciones de áreas comunes', href: '/areas-comunes', terms: ['reserva', 'reservacion', 'area', 'evento', 'deposito'], icon: Calendar },
  { label: 'Documentos, comunicados y encuestas', href: '/documentos', terms: ['documento', 'reglamento', 'biblioteca', 'comunicado', 'encuesta'], icon: FolderOpen },
  { label: 'Accesos, consignas y rondines', href: '/ares-seguridad', terms: ['acceso', 'visita', 'paquete', 'consigna', 'rondin', 'guardia', 'seguridad'], icon: Shield },
  { label: 'Proveedores, compras y cotizaciones', href: '/proveedores', terms: ['proveedor', 'compra', 'cotizacion', 'contrato'], icon: BriefcaseBusiness },
  { label: 'Acuerdos y próximas acciones', href: '/acuerdos', terms: ['acuerdo', 'pendiente', 'responsable', 'compromiso', 'consejo'], icon: Handshake },
];

export const Layout = ({ children }) => {
  const {
    user,
    logout,
    isDireccion,
    isSupervisor,
    token,
    api,
    activeCondominioId: contextCondominioId,
    setActiveCondominioId,
    getDefaultCondominioId,
    getAvailableModulos,
  } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const experienceConfig = EXPERIENCE_ROUTES.find((item) => location.pathname.startsWith(item.match)) || CORE_EXPERIENCE;
  const isDirectionOnlyWorkspace = [
    '/direccion',
    '/gestion-corporativa',
    '/control-integral',
    '/condominios',
    '/checklists/kpis',
    '/auditoria',
    '/configuracion',
    '/reportes/operacion-kpis',
  ].some((route) => location.pathname.startsWith(route));
  const hasAresStickyNavigation = location.pathname.startsWith('/ares-seguridad') || location.pathname.startsWith('/access-control');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [checklistsOpen, setChecklistsOpen] = useState(
    location.pathname.startsWith('/checklists')
  );
  // NEW: States for Juntas and Reportes collapsible menus
  const [juntasOpen, setJuntasOpen] = useState(
    location.pathname.startsWith('/juntas/calendario-actividades') ||
    location.pathname.startsWith('/calendario-operativo') ||
    location.pathname.startsWith('/minutas') ||
    location.pathname.startsWith('/acuerdos')
  );
  const [reportesOpen, setReportesOpen] = useState(
    location.pathname.startsWith('/reportes')
  );
  const [alertUnreadCount, setAlertUnreadCount] = useState(0);
  const [navigationAttention, setNavigationAttention] = useState({});
  const [navSearch, setNavSearch] = useState('');
  const [entitySearchResults, setEntitySearchResults] = useState([]);
  const [entitySearchLoading, setEntitySearchLoading] = useState(false);
  const [availableCondominios, setAvailableCondominios] = useState(() => Array.isArray(user?.condominios) ? user.condominios : []);
  const [condominiosLoading, setCondominiosLoading] = useState(false);
  const shownAlertIdsRef = useRef(new Set());
  const notificationPollingPrimedRef = useRef(false);
  const userRoles = activeProfileRoles(user);
  const isGuardOnly = isGuardOnlyProfile(user);
  const isAresExperience = isGuardOnly;
  const brandLogo = isAresExperience ? '/icons/ares-icon-192.png' : 'https://customer-assets.emergentagent.com/job_integral-property/artifacts/aaw7n3nd_FB_IMG_1769240570078.jpg';
  const brandName = isAresExperience ? 'Ares Seguridad' : 'AR Gestión';
  const hasActiveRoles = userRoles.length > 0;
  const isAdministratorView = !isDireccion() && !isSupervisor() && userRoles.includes('administrador');
  const isCouncilOnly = !isDireccion() && userRoles.includes('consejo') && !userRoles.some((role) => ['supervisor', 'administrador'].includes(role));
  const isDirectionProfile = isDireccion();
  const isEmergencyResponsible = userRoles.some((role) => ['direccion', 'supervisor', 'administrador'].includes(role));
  const homeHref = isGuardOnly ? '/ares-seguridad/caseta' : isCouncilOnly ? '/consejo' : isDirectionProfile ? '/direccion/resumen' : '/dashboard';

  const handleHomeNavigation = (event) => {
    event.preventDefault();
    setNavSearch('');
    setEntitySearchResults([]);
    setSidebarOpen(false);
    navigate(homeHref);
  };

  useEffect(() => {
    const unlockSound = () => {
      primeNotificationSound();
      window.removeEventListener('pointerdown', unlockSound);
      window.removeEventListener('keydown', unlockSound);
    };
    window.addEventListener('pointerdown', unlockSound, { passive: true });
    window.addEventListener('keydown', unlockSound);
    return () => {
      window.removeEventListener('pointerdown', unlockSound);
      window.removeEventListener('keydown', unlockSound);
    };
  }, []);

  useEffect(() => {
    delete document.body.dataset.arModule;
    document.body.classList.remove('ar-experience-active', 'ar-finance-active');
    if (experienceConfig) {
      document.body.dataset.arModule = experienceConfig.id;
      document.body.classList.add('ar-experience-active');
    } else {
      delete document.body.dataset.arModule;
    }
    return () => {
      delete document.body.dataset.arModule;
      document.body.classList.remove('ar-experience-active', 'ar-finance-active');
    };
  }, [experienceConfig]);

  useEffect(() => {
    const manifest = document.querySelector('link[rel="manifest"]');
    const icon = document.querySelector('link[rel="icon"]');
    const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const themeColor = document.querySelector('meta[name="theme-color"]');
    const previous = {
      manifest: manifest?.getAttribute('href'),
      icon: icon?.getAttribute('href'),
      appleIcon: appleIcon?.getAttribute('href'),
      appleTitle: appleTitle?.getAttribute('content'),
      themeColor: themeColor?.getAttribute('content'),
      title: document.title,
    };
    if (isAresExperience) {
      manifest?.setAttribute('href', '/manifest-ares.webmanifest');
      icon?.setAttribute('href', '/icons/ares-icon-192.png');
      icon?.setAttribute('type', 'image/png');
      appleIcon?.setAttribute('href', '/icons/ares-icon-192.png');
      appleTitle?.setAttribute('content', 'Ares Seguridad');
      themeColor?.setAttribute('content', '#090909');
      document.title = 'Ares Seguridad';
    }
    return () => {
      if (!isAresExperience) return;
      if (previous.manifest) manifest?.setAttribute('href', previous.manifest);
      if (previous.icon) icon?.setAttribute('href', previous.icon);
      if (previous.appleIcon) appleIcon?.setAttribute('href', previous.appleIcon);
      if (previous.appleTitle) appleTitle?.setAttribute('content', previous.appleTitle);
      if (previous.themeColor) themeColor?.setAttribute('content', previous.themeColor);
      document.title = previous.title;
    };
  }, [isAresExperience]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, location.search]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const activeCondominioId =
    contextCondominioId ||
    getDefaultCondominioId?.() ||
    user?.condominios_ids?.[0] ||
    null;
  const activeCondominio = availableCondominios.find((item) => item.id === activeCondominioId) || null;

  useEffect(() => {
    const query = navSearch.trim();
    if (!api || !activeCondominioId || query.length < 2 || isGuardOnly) {
      setEntitySearchResults([]);
      setEntitySearchLoading(false);
      return undefined;
    }
    let active = true;
    const timeoutId = window.setTimeout(() => {
      setEntitySearchLoading(true);
      api.get('/experience/search', { params: { condominio_id: activeCondominioId, q: query } })
        .then((response) => { if (active) setEntitySearchResults(response?.data?.results || []); })
        .catch(() => { if (active) setEntitySearchResults([]); })
        .finally(() => { if (active) setEntitySearchLoading(false); });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [api, activeCondominioId, navSearch, isGuardOnly]);
  const availableModulos = getAvailableModulos?.() || [];
  const activeModulePermissions =
    activeCondominioId && user?.effective_permissions?.by_condominio?.[activeCondominioId]?.modulos
      ? user.effective_permissions.by_condominio[activeCondominioId].modulos
      : {};

  const canViewModuloSafe = (modulo) => {
    try {
      if (isDireccion()) return true;
      if (!hasActiveRoles) return false;
      if (!activeCondominioId) return false;

      return Boolean(activeModulePermissions?.[modulo]?.can_view);
    } catch (e) {
      return false;
    }
  };

  const canShowModuloMenu = (modulo) => {
    if (!modulo) return true;

    try {
      if (isDireccion()) return true;
      if (!hasActiveRoles) return false;

      if (activeCondominioId && activeModulePermissions?.[modulo]) {
        return Boolean(activeModulePermissions?.[modulo]?.can_view);
      }

      if (Array.isArray(availableModulos) && availableModulos.includes(modulo)) {
        return true;
      }

      const byCondominio = user?.effective_permissions?.by_condominio || {};
      return Object.values(byCondominio).some((permissionSet) =>
        Boolean(permissionSet?.modulos?.[modulo]?.can_view)
      );
    } catch (e) {
      return false;
    }
  };

  // Filtrar navegación por roles
  const filteredNav = navigation.filter((item) => {
    if (!isDireccion() && !hasActiveRoles) {
      return false;
    }

    if (isGuardOnly) return item.href === '/ares-seguridad';
    if (isCouncilOnly) {
      return ['/consejo', '/conoce-ar/consejo', '/calendario-operativo', '/condo-ar/finanzas', '/documentos', '/alertas', '/acuerdos', '/minutas', '/incidencias', '/mantenimientos'].includes(item.href)
        && (!item.modulo || canShowModuloMenu(item.modulo));
    }

    const hasRoleAccess = (() => {
      if (item.roles.length === 0 || isDireccion()) {
        return true;
      }
      return item.roles.some((role) => userRoles.includes(role));
    })();

    if (!hasRoleAccess) {
      return false;
    }

    if (item.modulo) {
      return canShowModuloMenu(item.modulo);
    }
    return true;
  });

  useEffect(() => {
    // En producción API_URL puede ser una cadena vacía porque la API vive en el
    // mismo dominio. Las rutas relativas siguen siendo válidas y deben sondearse.
    if (!user) return undefined;

    let isMounted = true;

    const fetchAlerts = async () => {
      if (!localStorage.getItem('access_token')) return;

      try {
        const alertScope = activeCondominioId ? `?condominio_id=${encodeURIComponent(activeCondominioId)}` : '';
        const [response, countResponse] = await Promise.all([
          api.get(`/alertas${alertScope}`),
          api.get(`/alertas/no-leidas/count${alertScope}`),
        ]);
        const data = response.data;
        const countData = countResponse.data;
        if (!isMounted) return;

        const unreadAlerts = Array.isArray(data) ? data.filter((alerta) => !alerta.leida) : [];
        const actionableUnreadAlerts = unreadAlerts.filter(isOperationallyActionableAlert);
        const officialUnreadCount = actionableUnreadAlerts.length;
        setAlertUnreadCount(officialUnreadCount);
        const nextAttention = actionableUnreadAlerts.reduce((counts, alerta) => {
          const route = attentionRouteFor(alerta);
          counts[route] = (counts[route] || 0) + 1;
          counts['/alertas'] = (counts['/alertas'] || 0) + (route === '/alertas' ? 0 : 1);
          return counts;
        }, {});
        nextAttention['/alertas'] = officialUnreadCount;
        setNavigationAttention(nextAttention);

        const wasPollingPrimed = notificationPollingPrimedRef.current;
        let newUnreadAlerts = [];
        if (!wasPollingPrimed) {
          // Las alertas normales se consideran conocidas al abrir la sesión para
          // no inundar al usuario. Una emergencia no leída sí debe hacerse visible
          // aunque haya nacido antes de montar este layout.
          newUnreadAlerts = isEmergencyResponsible
            ? unreadAlerts.filter((alerta) => String(alerta.tipo || '').toLowerCase() === 'ares_emergencia')
            : [];
          unreadAlerts.forEach((alerta) => shownAlertIdsRef.current.add(alerta.id));
          notificationPollingPrimedRef.current = true;
        } else {
          newUnreadAlerts = unreadAlerts.filter((alerta) => !shownAlertIdsRef.current.has(alerta.id));
          newUnreadAlerts.forEach((alerta) => shownAlertIdsRef.current.add(alerta.id));

          if (newUnreadAlerts.length > 0) playNotificationSound();

          newUnreadAlerts
            .filter((alerta) => String(alerta.tipo || '').startsWith('asistencia_recordatorio'))
            .slice(0, 5)
            .forEach((alerta) => {
              toast.warning(alerta.titulo, {
                description: alerta.mensaje,
                duration: 10000,
              });
            });

          if ('Notification' in window && Notification.permission === 'granted') {
            newUnreadAlerts.slice(0, 3).forEach(async (alerta) => {
              const route = attentionRouteFor(alerta);
              const options = {
                body: String(alerta.mensaje || 'Tienes una nueva notificación').slice(0, 180),
                icon: isAresExperience ? '/icons/ares-icon-192.png' : '/icons/ar-icon-192.png',
                badge: isAresExperience ? '/icons/ares-icon-192.png' : '/icons/ar-icon-192.png',
                tag: `ar-alert-${alerta.id}`,
                data: { url: route },
              };
              try {
                const registration = await navigator.serviceWorker?.ready;
                if (registration) await registration.showNotification(alerta.titulo || brandName, options);
                else new Notification(alerta.titulo || brandName, options);
              } catch (_) {}
            });
          }
        }
        if (newUnreadAlerts.length > 0 && !wasPollingPrimed) playNotificationSound();
        if (isEmergencyResponsible) {
          newUnreadAlerts
            .filter((alerta) => String(alerta.tipo || '').toLowerCase() === 'ares_emergencia')
            .slice(0, 5)
            .forEach((alerta) => {
              const route = attentionRouteFor(alerta);
              toast.error(alerta.titulo || 'EMERGENCIA ARES', {
                id: `ares-emergency-${alerta.id}`,
                description: `${alerta.mensaje || 'Emergencia recibida en Ares Seguridad'} Abre el registro para atender y dar seguimiento.`,
                duration: Infinity,
                action: { label: 'Ver en Ares', onClick: () => navigate(route) },
              });
            });
        }
      } catch (error) {
        // El polling volverá a intentar sin interrumpir la operación.
      }
    };

    fetchAlerts();
    const intervalId = window.setInterval(fetchAlerts, isEmergencyResponsible ? 20000 : 60000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [user, activeCondominioId, api, isAresExperience, brandName, isEmergencyResponsible, navigate]);

  useEffect(() => {
    // En producción la API vive en el mismo dominio y
    // REACT_APP_BACKEND_URL puede ser una cadena vacía. En ese caso
    // `${API_URL}/api/...` es una URL relativa válida y no debe impedir
    // cargar el catálogo del selector global.
    if (!user) {
      setCondominiosLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const embeddedCondominios = Array.isArray(user.condominios) ? user.condominios.filter((item) => item?.id) : [];
    if (embeddedCondominios.length) setAvailableCondominios(embeddedCondominios);
    if (!(token || window.localStorage.getItem('access_token'))) {
      setCondominiosLoading(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(() => controller.abort(), 12000);
    setCondominiosLoading(true);
    api.get('/condominios', { signal: controller.signal })
      .then((response) => response.data)
      .then((rows) => {
        if (!Array.isArray(rows)) return;
        const ordered = [...rows].filter((item) => item?.id).sort((a, b) =>
          String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es-MX')
        );
        setAvailableCondominios(ordered);
        if (ordered.length && !ordered.some((item) => item.id === activeCondominioId)) {
          const preferred = getDefaultCondominioId?.();
          setActiveCondominioId?.(ordered.some((item) => item.id === preferred) ? preferred : ordered[0].id);
        }
      })
      .catch((error) => {
        if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED' && embeddedCondominios.length) {
          setAvailableCondominios((current) => current.length ? current : embeddedCondominios);
        }
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        setCondominiosLoading(false);
      });
    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [user, token, api]);

  const handleWorkingCondominioChange = (condominioId) => {
    const params = new URLSearchParams(location.search);
    params.set('condominio', condominioId);
    params.delete('condominio_id');
    if (!setActiveCondominioId?.(condominioId)) {
      return;
    }
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
    const selected = availableCondominios.find((item) => item.id === condominioId);
    toast.success(`Ahora trabajas en ${selected?.nombre || 'el condominio seleccionado'}`);
  };

  // Verificar si el módulo checklists está habilitado
  const checklistsHabilitado = !isGuardOnly && canShowModuloMenu('checklists');

  // Verificar módulos nuevos con permisos efectivos
  const juntasHabilitado = canShowModuloMenu('minutas') || canShowModuloMenu('acuerdos');
  // Reportes conserva la estructura antes ubicada en Finanzas y se presenta junto a Juntas.
  const reportesHabilitado = canShowModuloMenu('reportes');
  
  // Filtrar navegación de checklists por roles
  const filteredChecklistsNav = checklistsNav.filter((item) => {
    if (!canShowModuloMenu('checklists')) return false;
    if (isDireccion()) return true;
    if (isSupervisor() && item.roles.includes('supervisor')) return true;
    return item.roles.some((role) => userRoles.includes(role));
  });

  const filteredJuntasNav = juntasNav.filter((item) => {
    const modulo = item.href === '/minutas' ? 'minutas' : 'acuerdos';
    return canShowModuloMenu(modulo);
  });

  const filteredReportesNav = reportesNav.filter((item) => {
    if (!canShowModuloMenu('reportes')) return false;
    if (item.roles.length === 0) return true;
    if (isDireccion()) return true;
    return item.roles.some((role) => userRoles.includes(role));
  });

  const getInitials = (nombre, apellido) => {
    return `${nombre?.[0] || ''}${apellido?.[0] || ''}`.toUpperCase();
  };

  const getRoleBadge = () => {
    if (isDireccion()) return { label: 'Direccion', color: 'bg-[#7A1F2B] text-white' };
    const primaryRole = user?.primary_role;
    const roleColors = {
      supervisor: 'bg-blue-100 text-blue-700',
      administrador: 'bg-emerald-100 text-emerald-700',
      consejo: 'bg-amber-100 text-amber-700',
      guardia: 'bg-sky-100 text-sky-700',
      jardineria: 'bg-lime-100 text-lime-700',
      mantenimiento: 'bg-orange-100 text-orange-700',
    };
    const councilRole = user?.roles?.find((item) => item?.role === 'consejo' && item?.is_active !== false && (!activeCondominioId || item?.condominio_id === activeCondominioId));
    const councilLabels = { presidente: 'Presidencia', secretario: 'Secretaría', tesorero: 'Tesorería', vocal: 'Consejo' };
    return { 
      label: primaryRole === 'consejo'
        ? (councilLabels[councilRole?.council_position] || 'Consejo')
        : primaryRole === 'guardia'
          ? 'Caseta'
          : (primaryRole?.charAt(0).toUpperCase() + primaryRole?.slice(1) || 'Usuario'),
      color: roleColors[primaryRole] || 'bg-slate-100 text-slate-700'
    };
  };

  const roleBadge = getRoleBadge();
  const attentionForRoute = (href) => Number(navigationAttention[href] || 0);
  const attentionForRoutes = (routes) => routes.reduce((total, href) => total + attentionForRoute(href), 0);
  const renderAttentionBadge = (count, label = 'pendientes') => count > 0 ? (
    <span
      className="ml-auto inline-flex min-h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[#B4233A] px-1.5 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-white dark:ring-slate-900"
      title={`${count} ${label} por atender`}
      aria-label={`${count} ${label} por atender`}
    >
      {count > 99 ? '99+' : count}
    </span>
  ) : null;
  const directNavMap = new Map(filteredNav.map((item) => [item.href, item]));
  const getDirectNavItem = (href, name) => {
    const item = directNavMap.get(href);
    return item && name ? { ...item, name } : item;
  };
  const getJuntasNavItem = (href, name) => {
    const item = filteredJuntasNav.find((candidate) => candidate.href === href);
    return item && name ? { ...item, name } : item;
  };
  const directionGroups = [
    { title: 'Dirección', items: [
      { name: 'Resumen ejecutivo', href: '/direccion/resumen', icon: Sparkles },
      { name: 'Decisiones', href: '/direccion/decisiones', icon: ClipboardCheck },
      { name: 'Cotos', href: '/direccion/cotos', icon: Building2 },
      { name: 'Resultados', href: '/direccion/resultados', icon: BarChart3 },
      { name: 'Seguimiento', href: '/direccion/seguimiento', icon: Link2 },
      { name: 'Transparencia', href: '/direccion/transparencia', icon: ShieldCheck },
      { name: 'Reclutamiento', href: '/direccion/reclutamiento', icon: BriefcaseBusiness },
    ] },
    { title: 'Operación detallada', items: [
      getDirectNavItem('/incidencias', 'Incidencias'),
      getDirectNavItem('/mantenimientos', 'Mantenimiento'),
      { type: 'checklists', label: 'Checklists' },
      getDirectNavItem('/areas-comunes', 'Áreas comunes'),
      getDirectNavItem('/administracion/unidades', 'Unidades y residentes'),
      getDirectNavItem('/atencion-vecinal', 'Atención vecinal'),
      getDirectNavItem('/ares-seguridad', 'Ares Seguridad'),
      getDirectNavItem('/calendario-operativo', 'Calendario operativo'),
      getDirectNavItem('/asistencia', 'Asistencia'),
    ] },
    { title: 'Finanzas y recursos', items: [
      getDirectNavItem('/condo-ar/finanzas', 'Finanzas'),
      getDirectNavItem('/caja-chica', 'Caja chica'),
      getDirectNavItem('/proveedores', 'Proveedores'),
      getDirectNavItem('/cotizaciones', 'Cotizaciones'),
      getDirectNavItem('/inventario', 'Inventario'),
    ] },
    { title: 'Gobierno y comunicación', items: [
      getDirectNavItem('/gestiones-consejo', 'Gestiones del Consejo'),
      { type: 'juntas' }, { type: 'reportes' },
      getDirectNavItem('/documentos', 'Documentos y comunicados'),
      getDirectNavItem('/sanciones', 'Sanciones'),
      getDirectNavItem('/alertas', 'Notificaciones'),
    ] },
    { title: 'Administración del sistema', items: [
      getDirectNavItem('/control-integral', 'Control administrativo'),
      getDirectNavItem('/gestion-corporativa', 'Gestión operativa AR/Ares'),
      getDirectNavItem('/condominios', 'Condominios'),
      getDirectNavItem('/usuarios', 'Usuarios y permisos'),
      getDirectNavItem('/auditoria', 'Auditoría'),
      getDirectNavItem('/academia', 'Academia Grupo AR'),
      getDirectNavItem('/soporte', 'Soporte AR'),
      { name: 'Más funciones', href: '/direccion/mas', icon: Menu },
    ] },
  ];

  const administratorGroups = [
    { title: 'Inicio', items: [getDirectNavItem('/dashboard', 'Inicio'), getDirectNavItem('/continuidad', 'Pendientes y continuidad'), getDirectNavItem('/calendario-operativo', 'Calendario operativo'), getDirectNavItem('/alertas', 'Notificaciones'), getDirectNavItem('/asistencia', 'Mi asistencia')] },
    { title: 'Atención diaria', items: [getDirectNavItem('/gestiones-consejo', 'Gestiones del Consejo'), getDirectNavItem('/incidencias', 'Incidencias'), { type: 'checklists', label: 'Checklists' }, getDirectNavItem('/mantenimientos', 'Mantenimiento')] },
    { title: 'Comunidad', items: [getDirectNavItem('/areas-comunes', 'Áreas comunes'), getDirectNavItem('/administracion/unidades', 'Unidades y residentes'), getDirectNavItem('/atencion-vecinal', 'Atención vecinal'), getDirectNavItem('/sanciones', 'Sanciones')] },
    { title: 'Recursos y compras', items: [getDirectNavItem('/inventario', 'Inventario'), getDirectNavItem('/proveedores', 'Proveedores'), getDirectNavItem('/cotizaciones', 'Cotizaciones')] },
    { title: 'Juntas y reportes', items: [{ type: 'juntas' }, { type: 'reportes' }] },
    { title: 'Mis Finanzas', items: [getDirectNavItem('/condo-ar/finanzas', 'Mis Finanzas'), getDirectNavItem('/caja-chica', 'Caja chica')] },
    { title: 'Comunicación y archivos', items: [getDirectNavItem('/documentos', 'Biblioteca, comunicados y encuestas')] },
    { title: 'Ares Seguridad', items: [getDirectNavItem('/ares-seguridad', 'Accesos, consignas y evidencias')] },
    { title: 'Más', items: [getDirectNavItem('/condominios', 'Condominios'), getDirectNavItem('/usuarios', 'Usuarios y permisos'), getDirectNavItem('/auditoria', 'Auditoría'), getDirectNavItem('/academia', 'Academia Grupo AR'), getDirectNavItem('/soporte', 'Soporte AR')] },
  ];
  const councilGroups = [
    { title: 'Consejo', items: [
      { name: 'Resumen ejecutivo', href: '/consejo', icon: LayoutDashboard },
      { name: 'Gestiones', href: '/consejo/gestiones', icon: MessageCircle },
      { name: 'Decisiones', href: '/consejo/decisiones', icon: ClipboardCheck },
      { name: 'Finanzas', href: '/consejo/finanzas', icon: Landmark },
      { name: 'Seguimiento', href: '/consejo/seguimiento', icon: Link2 },
      { name: 'Documentos', href: '/consejo/documentos', icon: FileText },
      { name: 'Asistente IA', href: '/consejo/asistente', icon: Sparkles },
    ] },
    { title: 'Consulta detallada', items: [getDirectNavItem('/calendario-operativo', 'Agenda y vencimientos'), getDirectNavItem('/conoce-ar/consejo', 'Recorrido de bienvenida')] },
  ];

  const renderNavLink = (item, compact = false) => {
    if (navSearch && !item.name.toLocaleLowerCase('es-MX').includes(navSearch.toLocaleLowerCase('es-MX'))) return null;
    const isActive = location.pathname === item.href ||
      (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 ${compact ? 'px-3 py-2' : 'px-3 py-2.5'} rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? 'bg-[#7A1F2B] text-white'
            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
        }`}
      >
        <item.icon className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        <span className="min-w-0 flex-1 truncate">{item.name}</span>
        {renderAttentionBadge(attentionForRoute(item.href))}
      </Link>
    );
  };

  const renderSectionLabel = (title) => (
    <div className="px-3 pt-4 pb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
      {title}
    </div>
  );

  const renderChecklistsMenu = (label = 'Checklists') => (
    checklistsHabilitado && filteredChecklistsNav.length > 0 && (
      <Collapsible open={checklistsOpen} onOpenChange={setChecklistsOpen}>
        <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="h-5 w-5" />
            {label}
          </div>
          {renderAttentionBadge(attentionForRoutes(filteredChecklistsNav.map((item) => item.href)))}
          <ChevronRight className={`h-4 w-4 transition-transform ${checklistsOpen ? 'rotate-90' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="pl-4 space-y-1 mt-1">
          {filteredChecklistsNav.map((item) => renderNavLink(item, true))}
        </CollapsibleContent>
      </Collapsible>
    )
  );

  const renderJuntasMenu = () => (
    juntasHabilitado && filteredJuntasNav.length > 0 && (
      <Collapsible open={juntasOpen} onOpenChange={setJuntasOpen}>
        <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5" />
            Juntas
          </div>
          {renderAttentionBadge(attentionForRoutes(filteredJuntasNav.map((item) => item.href)))}
          <ChevronRight className={`h-4 w-4 transition-transform ${juntasOpen ? 'rotate-90' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="pl-4 space-y-1 mt-1">
          {filteredJuntasNav.map((item) => renderNavLink(item, true))}
        </CollapsibleContent>
      </Collapsible>
    )
  );

  const renderReportesMenu = () => (
    reportesHabilitado && filteredReportesNav.length > 0
      ? <>{filteredReportesNav.map((item) => renderNavLink(item))}</>
      : null
  );

  const renderDirectionNavigation = () => (
    <>
      {directionGroups.map((group, groupIndex) => {
        const renderedItems = group.items
          .map((item) => {
            if (!item) return null;
            if (item.type === 'checklists') return renderChecklistsMenu(item.label);
            if (item.type === 'juntas') return renderJuntasMenu();
            if (item.type === 'reportes') return renderReportesMenu();
            return renderNavLink(item);
          })
          .filter(Boolean);

        if (renderedItems.length === 0) return null;

        return (
          <React.Fragment key={group.title || `direction-group-${groupIndex}`}>
            {group.title && renderSectionLabel(group.title)}
            {renderedItems}
          </React.Fragment>
        );
      })}
    </>
  );

  const renderGroupedNavigation = (groups) => (
    <>
      {groups.map((group, groupIndex) => {
        const renderedItems = group.items
          .map((item) => {
            if (!item) return null;
            if (item.type === 'checklists') return renderChecklistsMenu(item.label);
            if (item.type === 'juntas') return renderJuntasMenu();
            if (item.type === 'reportes') return renderReportesMenu();
            return renderNavLink(item);
          })
          .filter(Boolean);

        if (renderedItems.length === 0) return null;

        return (
          <React.Fragment key={group.title || `group-${groupIndex}`}>
            {group.title && renderSectionLabel(group.title)}
            {renderedItems}
          </React.Fragment>
        );
      })}
    </>
  );

  const allVisibleRoutes = [
    ...filteredNav,
    ...filteredChecklistsNav,
    ...filteredJuntasNav,
    ...filteredReportesNav,
  ];
  const activeRoute = [...allVisibleRoutes]
    .sort((a, b) => b.href.length - a.href.length)
    .find((item) => location.pathname === item.href || (item.href !== '/dashboard' && location.pathname.startsWith(item.href)));
  const activePageTitle = activeRoute?.name || 'AR Gestión';
  const quickCaptureItems = [
    { label: 'Ingreso o pago', href: '/condo-ar/finanzas?seccion=ingresos&accion=nuevo', modulo: 'financiero' },
    { label: 'Gasto o comprobante', href: '/caja-chica?accion=gasto', modulo: 'caja_chica' },
    { label: 'Incidencia', href: '/incidencias?accion=nueva', modulo: 'incidencias' },
    { label: 'Mantenimiento', href: '/mantenimientos?accion=nuevo', modulo: 'mantenimientos' },
    { label: 'Residente, vehículo o unidad', href: '/administracion/unidades?accion=registrar', modulo: null },
    { label: 'Reservación o área común', href: '/areas-comunes?accion=nueva', modulo: 'areas_comunes' },
    { label: 'Proveedor o cotización', href: '/proveedores?accion=nuevo', modulo: 'proveedores' },
    { label: 'Comunicado o encuesta', href: '/documentos?accion=nuevo', modulo: 'documentos' },
    { label: 'Inspección o checklist', href: '/checklists', modulo: 'checklists' },
    { label: 'Registrar acceso de seguridad', href: '/ares-seguridad/caseta', modulo: 'access_control' },
    { label: 'Acuerdo o seguimiento', href: '/acuerdos?accion=nuevo', modulo: null },
    { label: 'Registrar asistencia', href: '/asistencia/registrar', modulo: 'asistencia' },
  ].filter((item) => !item.modulo || canViewModuloSafe(item.modulo));
  const mobileItems = isDirectionProfile ? [
    { name: 'Inicio', href: '/direccion/resumen', icon: Sparkles },
    { name: 'Cotos', href: '/direccion/cotos', icon: Building2 },
    { name: 'Resultados', href: '/direccion/resultados', icon: BarChart3 },
  ] : isCouncilOnly ? [
    { name: 'Inicio', href: '/consejo', icon: LayoutDashboard },
    { name: 'Gestiones', href: '/consejo/gestiones', icon: MessageCircle },
    { name: 'Decisiones', href: '/consejo/decisiones', icon: ClipboardCheck },
    { name: 'Seguimiento', href: '/consejo/seguimiento', icon: Link2 },
  ].filter(Boolean) : [
    getDirectNavItem('/dashboard', 'Inicio'),
    getDirectNavItem('/administracion/unidades', 'Comunidad'),
    getDirectNavItem('/condo-ar/finanzas', 'Mis Finanzas') || getDirectNavItem('/alertas', 'Notificaciones'),
  ].filter(Boolean);
  const isMobileRouteActive = (href) => {
    const [pathname, hash] = href.split('#');
    if (hash) return location.pathname === pathname && location.hash === `#${hash}`;
    return location.pathname === pathname || (pathname !== '/dashboard' && location.pathname.startsWith(pathname));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo Oficial AR */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200 dark:border-slate-800">
            <Link to={homeHref} onClick={handleHomeNavigation} className="flex items-center gap-3" aria-label={`Ir al inicio de ${brandName}`}>
              <img 
                src={brandLogo}
                alt={brandName}
                className="w-10 h-10 object-contain"
                data-testid="sidebar-ar-logo"
              />
              <span className="font-semibold text-slate-900 dark:text-white tracking-tight">
                {brandName}
              </span>
            </Link>
            <button
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Cerrar menú de navegación"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <div className="px-3 pb-3">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                <input
                  type="search"
                  name="ar-navigation-search"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  aria-label="Buscar en AR Gestión"
                  value={navSearch}
                  onChange={(event) => setNavSearch(event.target.value)}
                  placeholder="Buscar módulo, casa, placa o pendiente"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-[#8A1C2C] focus:ring-2 focus:ring-[#8A1C2C]/10"
                />
              </label>
              {navSearch.trim().length >= 2 ? <div className="mt-2 rounded-2xl border border-[#ead7da] bg-[#fcf8f7] p-2">
                <p className="px-2 pb-1 pt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A1C2C]">Resultados y acciones</p>
                {entitySearchLoading ? <p className="px-2 py-2 text-xs text-slate-500">Buscando en el condominio…</p> : null}
                {entitySearchResults.map((item) => {
                  const ResultIcon = item.type === 'proveedor' ? BriefcaseBusiness : item.type === 'vehiculo' ? QrCode : item.type === 'censo' ? Users : Home;
                  return <Link key={item.key} to={item.route} onClick={() => { setSidebarOpen(false); setNavSearch(''); }} className="flex items-start gap-2 rounded-xl bg-white px-2 py-2 text-xs text-slate-700 shadow-sm hover:text-[#8A1C2C]"><ResultIcon className="mt-0.5 h-4 w-4 shrink-0" /><span className="min-w-0 flex-1"><span className="block truncate font-semibold">{item.title}</span><span className="block truncate text-[11px] font-normal text-slate-500">{item.subtitle}</span></span><ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0" /></Link>;
                })}
                {OPERATION_SEARCH_SHORTCUTS.filter((item) => {
                  const query = navSearch.trim().toLocaleLowerCase('es-MX');
                  return item.label.toLocaleLowerCase('es-MX').includes(query) || item.terms.some((term) => term.includes(query) || query.includes(term));
                }).slice(0, 4).map((item) => <Link key={item.href} to={item.href} onClick={() => { setSidebarOpen(false); setNavSearch(''); }} className="flex items-center gap-2 rounded-xl px-2 py-2 text-xs font-semibold text-slate-700 hover:bg-white hover:text-[#8A1C2C]"><item.icon className="h-4 w-4" /><span>{item.label}</span><ChevronRight className="ml-auto h-3.5 w-3.5" /></Link>)}
                <Link to={`/administracion/unidades?q=${encodeURIComponent(navSearch.trim())}`} onClick={() => { setSidebarOpen(false); setNavSearch(''); }} className="mt-1 flex items-center gap-2 rounded-xl bg-white px-2 py-2 text-xs font-semibold text-[#8A1C2C] shadow-sm"><Search className="h-4 w-4" /><span className="min-w-0 flex-1 truncate">Buscar “{navSearch.trim()}” en unidades</span><ChevronRight className="h-3.5 w-3.5" /></Link>
              </div> : null}
            </div>
            <nav className="px-3 space-y-1">
              {isCouncilOnly ? renderGroupedNavigation(councilGroups) : isDireccion() ? renderDirectionNavigation() : isSupervisor() || isAdministratorView ? (
                renderGroupedNavigation(administratorGroups)
              ) : (
                <>
                  {filteredNav.map((item) => renderNavLink(item))}
              
                  {/* Checklists Section - Solo si el módulo está habilitado */}
                  {checklistsHabilitado && filteredChecklistsNav.length > 0 && (
                    <Collapsible open={checklistsOpen} onOpenChange={setChecklistsOpen}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
                        <div className="flex items-center gap-3">
                          <ClipboardCheck className="h-5 w-5" />
                          Checklists
                        </div>
                        {renderAttentionBadge(attentionForRoutes(filteredChecklistsNav.map((item) => item.href)))}
                        <ChevronRight className={`h-4 w-4 transition-transform ${checklistsOpen ? 'rotate-90' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pl-4 space-y-1 mt-1">
                        {filteredChecklistsNav.map((item) => renderNavLink(item, true))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
              
                  {/* NEW: Juntas Section */}
                  {juntasHabilitado && filteredJuntasNav.length > 0 && (
                  <Collapsible open={juntasOpen} onOpenChange={setJuntasOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5" />
                        Juntas
                      </div>
                      {renderAttentionBadge(attentionForRoutes(filteredJuntasNav.map((item) => item.href)))}
                      <ChevronRight className={`h-4 w-4 transition-transform ${juntasOpen ? 'rotate-90' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-4 space-y-1 mt-1">
                      {filteredJuntasNav.map((item) => renderNavLink(item, true))}
                    </CollapsibleContent>
                  </Collapsible>
                  )}
              
                  {/* NEW: Reportes Section */}
                  {reportesHabilitado && filteredReportesNav.length > 0 && (
                  <Collapsible open={reportesOpen} onOpenChange={setReportesOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
                      <div className="flex items-center gap-3">
                        <FileBarChart className="h-5 w-5" />
                        Reportes
                      </div>
                      {renderAttentionBadge(attentionForRoutes(filteredReportesNav.map((item) => item.href)))}
                      <ChevronRight className={`h-4 w-4 transition-transform ${reportesOpen ? 'rotate-90' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-4 space-y-1 mt-1">
                      {filteredReportesNav.map((item) => renderNavLink(item, true))}
                    </CollapsibleContent>
                  </Collapsible>
                  )}
                </>
              )}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="bg-[#7A1F2B] text-white text-xs">
                  {getInitials(user?.nombre, user?.apellido)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                  {user?.nombre} {user?.apellido}
                </p>
                <Badge className={`text-xs ${roleBadge.color}`}>
                  {roleBadge.label}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        <InteractionSafety scope={activeCondominioId || 'corporativo'} userId={user?.id || 'usuario'} />
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between h-full px-4 sm:px-6">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </button>
              {/* Logo en header para móvil */}
              <Link to={homeHref} onClick={handleHomeNavigation} className="lg:hidden flex items-center gap-2" aria-label={`Ir al inicio de ${brandName}`}>
                <img 
                  src={brandLogo}
                  alt={brandName}
                  className="w-8 h-8 object-contain"
                  data-testid="header-ar-logo"
                />
                <span className="font-semibold text-slate-900 dark:text-white text-sm">
                  {brandName}
                </span>
              </Link>
            </div>

            <div className="hidden min-w-0 flex-1 items-center gap-4 px-5 lg:flex">
              <p className="min-w-[90px] truncate text-sm font-semibold text-slate-900 dark:text-white">{activePageTitle}</p>
              {!isDirectionOnlyWorkspace ? <>
                <span className="h-8 w-px shrink-0 bg-slate-200" aria-hidden="true" />
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#8A1C2C] text-white">
                  <Building2 className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="hidden min-w-0 xl:block">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-400">Condominio</p>
                  <p className="max-w-[180px] truncate text-sm font-semibold text-slate-950">
                    {activeCondominio?.nombre || (condominiosLoading ? 'Consultando…' : 'Selecciona un condominio')}
                  </p>
                </div>
                <label className="min-w-0 flex-1 lg:max-w-[240px] xl:max-w-[300px]">
                  <span className="sr-only">Cambiar condominio de trabajo</span>
                  <select
                    value={activeCondominioId || ''}
                    onChange={(event) => handleWorkingCondominioChange(event.target.value)}
                    disabled={availableCondominios.length === 0}
                    className="h-10 w-full rounded-xl border border-[#e4d3d6] bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-[#8A1C2C] focus:ring-2 focus:ring-[#8A1C2C]/15 disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid="desktop-condominio-selector"
                  >
                    <option value="" disabled>Selecciona un condominio</option>
                    {availableCondominios.map((condominio) => <option key={condominio.id} value={condominio.id}>{condominio.nombre}</option>)}
                  </select>
                </label>
              </> : null}
            </div>

            <div className="flex-1 lg:hidden" />

            <div className="flex items-center gap-2">
              {/* Theme toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9"
                data-testid="theme-toggle"
              >
                {theme === 'light' ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <Sun className="h-4 w-4" />
                )}
              </Button>

              {!isGuardOnly && !isCouncilOnly && <div className="hidden lg:block"><DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="h-10 gap-2 bg-[#8A1C2C] px-3 text-white hover:bg-[#741523]" aria-label="Registrar una actividad">
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">Registrar</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <div className="px-2 py-1.5"><p className="text-sm font-semibold">¿Qué deseas registrar?</p><p className="text-xs text-slate-500">Se conservará el condominio activo y abrirás el expediente correspondiente.</p></div>
                  <DropdownMenuSeparator />
                  {quickCaptureItems.map((item) => <DropdownMenuItem key={item.href} onClick={() => navigate(item.href)}>{item.label}</DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu></div>}

              {/* Notifications */}
              {!isGuardOnly && <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 relative"
                onClick={() => navigate('/alertas')}
                data-testid="notifications-btn"
              >
                <Bell className="h-4 w-4" />
                {alertUnreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-[#8B2332] px-1.5 text-[10px] font-semibold text-white">
                    {alertUnreadCount > 9 ? '9+' : alertUnreadCount}
                  </span>
                )}
              </Button>}

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-9 gap-2 px-2" data-testid="user-menu">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={user?.avatar_url} />
                      <AvatarFallback className="bg-[#7A1F2B] text-white text-xs">
                        {getInitials(user?.nombre, user?.apellido)}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.nombre} {user?.apellido}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/perfil')}>
                    <Users className="mr-2 h-4 w-4" />
                    Mi Perfil
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {!isDirectionOnlyWorkspace && <section className="sticky top-16 z-20 border-b border-[#eadde0] bg-white/95 backdrop-blur lg:hidden" aria-label="Condominio de trabajo">
          <div className="mx-auto flex max-w-7xl items-center gap-2 px-3 py-2 sm:justify-between sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#8A1C2C] text-white">
                <Building2 className="h-4 w-4" aria-hidden="true" />
              </span>
              <div className="hidden min-w-0 sm:block">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Condominio</p>
                <p className="truncate text-sm font-semibold text-slate-950">
                  {activeCondominio?.nombre || (condominiosLoading ? 'Consultando condominios…' : 'Selecciona un condominio para comenzar')}
                </p>
              </div>
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:flex-initial">
              <label className="flex min-w-0 flex-1 items-center sm:w-[320px] sm:flex-initial">
              <span className="sr-only">Cambiar condominio de trabajo</span>
              <select
                value={activeCondominioId || ''}
                onChange={(event) => handleWorkingCondominioChange(event.target.value)}
                disabled={availableCondominios.length === 0}
                className="h-10 w-full rounded-xl border border-[#e4d3d6] bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-[#8A1C2C] focus:ring-2 focus:ring-[#8A1C2C]/15 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="global-condominio-selector"
              >
                <option value="" disabled>Selecciona un condominio</option>
                {availableCondominios.map((condominio) => (
                  <option key={condominio.id} value={condominio.id}>{condominio.nombre}</option>
                ))}
              </select>
              </label>
            </div>
          </div>
        </section>}

        {/* Page content */}
        <main className={`mobile-platform-shell min-w-0 ${hasAresStickyNavigation ? 'overflow-x-clip' : 'overflow-x-hidden'} px-2 pb-24 pt-3 sm:p-5 lg:p-6 ${experienceConfig ? 'ar-experience-canvas' : ''}`}>
          <div className={`min-w-0 max-w-7xl mx-auto ${hasAresStickyNavigation ? '' : 'animate-fade-in'} ${experienceConfig ? 'ar-module-experience' : ''}`} data-ar-module={experienceConfig?.id || undefined}>
            {children}
          </div>
        </main>
        {!isGuardOnly ? <nav className="ar-mobile-navigation fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-2 pb-[max(.45rem,env(safe-area-inset-bottom))] pt-1.5 shadow-[0_-10px_30px_rgba(15,23,42,.10)] backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/95 lg:hidden" aria-label="Navegación principal móvil">
          <div className="mx-auto grid max-w-lg grid-cols-5 items-end gap-1">
            {mobileItems.slice(0, 2).map((item) => <Link key={item.href} to={item.href} aria-current={isMobileRouteActive(item.href) ? 'page' : undefined} className={`relative flex min-h-[52px] flex-col items-center justify-center gap-1 rounded-xl px-1 text-[10px] font-semibold ${isMobileRouteActive(item.href) ? 'bg-[#8A1C2C]/10 text-[#8A1C2C]' : 'text-slate-500'}`}><item.icon className="h-5 w-5" /><span className="max-w-full truncate">{item.name}</span>{attentionForRoute(item.href) > 0 ? <span className="absolute right-2 top-1.5 h-2 w-2 rounded-full bg-rose-600 ring-2 ring-white" /> : null}</Link>)}
            {isDirectionProfile ? <Link to="/direccion/decisiones" className="mx-auto -mt-5 flex h-14 w-14 flex-col items-center justify-center rounded-2xl bg-[#8A1C2C] text-white shadow-lg shadow-[#8A1C2C]/30" aria-label="Abrir decisiones"><ClipboardCheck className="h-6 w-6" /><span className="mt-0.5 text-[9px] font-bold">Decidir</span></Link> : isCouncilOnly ? <Link to="/consejo/asistente" className="mx-auto -mt-5 flex h-14 w-14 flex-col items-center justify-center rounded-2xl bg-[#8A1C2C] text-white shadow-lg shadow-[#8A1C2C]/30" aria-label="Abrir asistente"><Sparkles className="h-6 w-6" /><span className="mt-0.5 text-[9px] font-bold">Asistente</span></Link> : <DropdownMenu><DropdownMenuTrigger asChild><button type="button" className="mx-auto -mt-5 flex h-14 w-14 flex-col items-center justify-center rounded-2xl bg-[#8A1C2C] text-white shadow-lg shadow-[#8A1C2C]/30" aria-label="Registrar una actividad"><Plus className="h-6 w-6" /><span className="mt-0.5 text-[9px] font-bold">Registrar</span></button></DropdownMenuTrigger><DropdownMenuContent align="center" side="top" className="mb-3 max-h-[70vh] w-72 overflow-y-auto"><div className="px-2 py-1.5"><p className="text-sm font-semibold">¿Qué deseas registrar?</p><p className="text-xs text-slate-500">Se conservará el condominio activo.</p></div><DropdownMenuSeparator />{quickCaptureItems.map((item) => <DropdownMenuItem key={item.href} onClick={() => navigate(item.href)}>{item.label}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu>}
            {mobileItems.slice(2, 3).map((item) => <Link key={item.href} to={item.href} aria-current={isMobileRouteActive(item.href) ? 'page' : undefined} className={`relative flex min-h-[52px] flex-col items-center justify-center gap-1 rounded-xl px-1 text-[10px] font-semibold ${isMobileRouteActive(item.href) ? 'bg-[#8A1C2C]/10 text-[#8A1C2C]' : 'text-slate-500'}`}><item.icon className="h-5 w-5" /><span className="max-w-full truncate">{item.name}</span></Link>)}
            {isDirectionProfile ? <Link to="/direccion/mas" aria-current={isMobileRouteActive('/direccion/mas') ? 'page' : undefined} className={`flex min-h-[52px] flex-col items-center justify-center gap-1 rounded-xl px-1 text-[10px] font-semibold ${isMobileRouteActive('/direccion/mas') ? 'bg-[#8A1C2C]/10 text-[#8A1C2C]' : 'text-slate-500'}`}><Menu className="h-5 w-5" /><span>Más</span></Link> : <button type="button" onClick={() => setSidebarOpen(true)} className="flex min-h-[52px] flex-col items-center justify-center gap-1 rounded-xl px-1 text-[10px] font-semibold text-slate-500" aria-label="Abrir todos los módulos"><Menu className="h-5 w-5" /><span>Más</span></button>}
          </div>
        </nav> : null}
        {['direccion', 'supervisor', 'administrador', 'consejo'].some((role) => userRoles.includes(role)) ? <GlobalSupportCoach condominioId={activeCondominio?.id} condominioName={activeCondominio?.nombre} /> : null}
        <WebPushPrompt api={api} isAres={isAresExperience} />
      </div>
    </div>
  );
};

export default Layout;
